import React, { useState } from 'react';
import { uploadService } from '../../../services';
import './AttachmentUpload.css';

const AttachmentUpload = ({ 
  photos = [], 
  documents = [], 
  onPhotosChange, 
  onDocumentsChange,
  maxPhotos = 5,
  maxDocuments = 3,
  maxPhotoSize = 10, // MB
  maxDocumentSize = 25, // MB
  showPhotos = true,
  showDocuments = true,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file, type) => {
    const maxSize = type === 'photo' ? maxPhotoSize : maxDocumentSize;
    const maxSizeBytes = maxSize * 1024 * 1024;
    
    if (file.size > maxSizeBytes) {
      return `File size must be less than ${maxSize}MB`;
    }
    
    if (type === 'photo') {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return 'Only image files (JPEG, PNG, GIF, WebP) are allowed for photos';
      }
    } else {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        return 'Only PDF and Word documents are allowed';
      }
    }
    
    return null;
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check limits
    if (photos.length + files.length > maxPhotos) {
      setError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        const validationError = validateFile(file, 'photo');
        if (validationError) {
          throw new Error(validationError);
        }

        const response = await uploadService.uploadFile(file, 'quote-photos');
        const uploadedFile = response.files[0];
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          filename: uploadedFile.filename,
          url: `${process.env.REACT_APP_API_BASE_URL.replace('/api', '')}${uploadedFile.url}`,
          size: file.size,
          type: 'photo'
        };
      });

      const newPhotos = await Promise.all(uploadPromises);
      onPhotosChange([...photos, ...newPhotos]);
    } catch (error) {
      console.error('Photo upload error:', error);
      setError(error.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check limits
    if (documents.length + files.length > maxDocuments) {
      setError(`Maximum ${maxDocuments} documents allowed`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        const validationError = validateFile(file, 'document');
        if (validationError) {
          throw new Error(validationError);
        }

        const response = await uploadService.uploadFile(file, 'quote-documents');
        const uploadedFile = response.files[0];
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          filename: uploadedFile.filename,
          url: `${process.env.REACT_APP_API_BASE_URL.replace('/api', '')}${uploadedFile.url}`,
          size: file.size,
          type: 'document'
        };
      });

      const newDocuments = await Promise.all(uploadPromises);
      onDocumentsChange([...documents, ...newDocuments]);
    } catch (error) {
      console.error('Document upload error:', error);
      setError(error.message || 'Failed to upload documents');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const removePhoto = (photoId) => {
    onPhotosChange(photos.filter(photo => photo.id !== photoId));
  };

  const removeDocument = (docId) => {
    onDocumentsChange(documents.filter(doc => doc.id !== docId));
  };

  return (
    <div className={`attachment-upload ${className}`}>
      <div className="form-section">
        <h4>Attachments</h4>
        
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="attachment-grid">
          {showPhotos && (
            <div className="attachment-group">
              <div className="attachment-header">
                <label className="attachment-label">Photos</label>
                <span className="attachment-info">
                  {photos.length}/{maxPhotos} files (max {maxPhotoSize}MB each)
                </span>
              </div>
              
              <div className="file-upload-container">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploading || photos.length >= maxPhotos}
                  className="file-input"
                  id="photo-upload"
                />
                <label 
                  htmlFor="photo-upload" 
                  className={`file-upload-button ${uploading || photos.length >= maxPhotos ? 'disabled' : ''}`}
                >
                  {uploading ? '📤 Uploading...' : '📷 Choose Photos'}
                </label>
              </div>

              {photos.length > 0 && (
                <div className="file-list">
                  {photos.map(photo => (
                    <div key={photo.id} className="file-item">
                      <div className="file-info">
                        <span className="file-icon">🖼️</span>
                        <div className="file-details">
                          <span className="file-name">{photo.name}</span>
                          <span className="file-size">{formatFileSize(photo.size)}</span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => removePhoto(photo.id)}
                        className="remove-button"
                        title="Remove photo"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {showDocuments && (
            <div className="attachment-group">
              <div className="attachment-header">
                <label className="attachment-label">Documents</label>
                <span className="attachment-info">
                  {documents.length}/{maxDocuments} files (max {maxDocumentSize}MB each)
                </span>
              </div>
              
              <div className="file-upload-container">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={handleDocumentUpload}
                  disabled={uploading || documents.length >= maxDocuments}
                  className="file-input"
                  id="document-upload"
                />
                <label 
                  htmlFor="document-upload" 
                  className={`file-upload-button ${uploading || documents.length >= maxDocuments ? 'disabled' : ''}`}
                >
                  {uploading ? '📤 Uploading...' : '📄 Choose Documents'}
                </label>
              </div>

              {documents.length > 0 && (
                <div className="file-list">
                  {documents.map(doc => (
                    <div key={doc.id} className="file-item">
                      <div className="file-info">
                        <span className="file-icon">📄</span>
                        <div className="file-details">
                          <span className="file-name">{doc.name}</span>
                          <span className="file-size">{formatFileSize(doc.size)}</span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => removeDocument(doc.id)}
                        className="remove-button"
                        title="Remove document"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentUpload;
